import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import GroupsIcon from "@mui/icons-material/Groups";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import EventIcon from "@mui/icons-material/Event";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import SensorOccupiedIcon from "@mui/icons-material/SensorOccupied";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Link as RouterLink } from "react-router-dom";
import { LeyendaRecolectores } from "./LeyendaRecolectores";
import { AvatarPersona } from "../documentacion/AvatarPersona";
import { authFetch } from "../../services/apiClient";
import { cpPorFoto, updateParteGrupoEstado } from "../../services/apis";
import { hasAuthSession } from "../../services/authStorage";
import {
	formatDateTimeEs,
	formatScoreOverHundred,
} from "../../utils/formatters";

export const CuadrillaCard = ({
	info,
	parentDialogOpen = true,
	capabilities,
	onRecolectorAvatarUploaded,
	onEstadoParteUpdated,
	onRefetchDetalle,
	personaReturnState = null,
}) => {
	const sortRecolectoresByName = (recolectores = []) =>
		[...(Array.isArray(recolectores) ? recolectores : [])].sort((left, right) => {
			const leftDisplayName = `${String(left?.nombre_recolector || "").trim()} ${String(
				left?.apellidos_recolector || ""
			).trim()}`
				.trim()
				.replace(/\s+/g, " ");
			const rightDisplayName = `${String(right?.nombre_recolector || "").trim()} ${String(
				right?.apellidos_recolector || ""
			).trim()}`
				.trim()
				.replace(/\s+/g, " ");

			return leftDisplayName.localeCompare(rightDisplayName, "es", {
				sensitivity: "base",
			});
		});

	const [fichajePreview, setFichajePreview] = useState(null);
	const [fichajePreviewSrc, setFichajePreviewSrc] = useState("");
	const [fichajePreviewLoading, setFichajePreviewLoading] = useState(false);
	const [fichajePreviewError, setFichajePreviewError] = useState("");
	const [cpUploading, setCpUploading] = useState(false);
	const [cpResult, setCpResult] = useState(null);
	const fileInputRef = useRef(null);

	const idCuadrilla = info?.datos_cuadrilla?.id_cuadrilla ?? null;
	const [estadoParte, setEstadoParte] = useState({
		validado: Number(info?.datos_cuadrilla?.validado ?? 0),
		revisado: Number(info?.datos_cuadrilla?.revisado ?? 0),
	});

	useEffect(() => {
		setEstadoParte({
			validado: Number(info?.datos_cuadrilla?.validado ?? 0),
			revisado: Number(info?.datos_cuadrilla?.revisado ?? 0),
		});
	}, [info?.datos_cuadrilla?.validado, info?.datos_cuadrilla?.revisado]);

	const suplantacionUi = useMemo(() => {
		const reason = typeof cpResult?.reason === "string" ? cpResult.reason : "";

		if (reason === "liveness_failed") {
			return {
				severity: "warning",
				title: "Liveness fallido",
				description: "La comprobación de vida no ha superado el umbral mínimo.",
			};
		}

		if (reason === "no_face") {
			return {
				severity: "warning",
				title: "No se detectó cara",
				description: "No se detectó una cara en la imagen enviada.",
			};
		}

		if (reason === "not_recognized") {
			return {
				severity: "error",
				title: "No reconocido",
				description: "No se encontró coincidencia válida para la foto.",
			};
		}

		if (reason === "recognized_not_registered") {
			return {
				severity: "error",
				title: "Reconocido pero no registrado",
				description: "Se reconoció a alguien, pero no existe en la base de datos.",
			};
		}

		if (reason === "no_pertenece_parte") {
			return {
				severity: "error",
				title: "No pertenece al parte",
				description: "Se reconoció a alguien que no está registrado en este parte de grupo.",
			};
		}

		return {
			severity: "error",
			title: "",
			description: "",
		};
	}, [cpResult?.reason]);

	const handleEstadoChange = async ({ field, value }) => {
		if (!idCuadrilla) return;

		const numericValue = Number(value);
		const previousEstadoParte = { ...estadoParte };

		setEstadoParte((current) => ({
			...current,
			[field]: numericValue,
		}));

		try {
			await updateParteGrupoEstado({
				id_cuadrilla: idCuadrilla,
				[field]: numericValue,
			});

			if (typeof onEstadoParteUpdated === "function") {
				onEstadoParteUpdated({
					idCuadrilla,
					changes: {
						[field]: numericValue,
					},
				});
			}

			if (typeof onRefetchDetalle === "function") {
				await onRefetchDetalle();
			}
		} catch (error) {
			setEstadoParte(previousEstadoParte);
			console.error("Error al actualizar validado/revisado:", error);
		}
	};

	const cpDetails = useMemo(() => {
		if (!cpResult) return [];

		const entries = [];

		if (typeof cpResult?.reason === "string" && cpResult.reason.trim()) {
			entries.push({ key: "reason", label: "reason", value: cpResult.reason.trim() });
		}

		if (cpResult?.liveness_score !== undefined && cpResult?.liveness_score !== null) {
			entries.push({
				key: "liveness_score",
				label: "liveness",
				value: formatScoreOverHundred(cpResult.liveness_score),
			});
		}

		if (cpResult?.probability !== undefined && cpResult?.probability !== null) {
			entries.push({
				key: "probability",
				label: "prob.",
				value: formatScoreOverHundred(cpResult.probability),
			});
		}

		if (typeof cpResult?.uuid_luxand === "string" && cpResult.uuid_luxand.trim()) {
			entries.push({
				key: "uuid_luxand",
				label: "uuid",
				value: cpResult.uuid_luxand.trim(),
			});
		} else if (typeof cpResult?.uuid === "string" && cpResult.uuid.trim()) {
			entries.push({ key: "uuid", label: "uuid", value: cpResult.uuid.trim() });
		}

		if (cpResult?.id_usuario_detectado !== undefined && cpResult?.id_usuario_detectado !== null) {
			entries.push({
				key: "id_usuario_detectado",
				label: "detectado",
				value: String(cpResult.id_usuario_detectado),
			});
		}

		return entries;
	}, [cpResult]);

	const getColor = (recolector) => {
		if (recolector.comprobado === 0) {
			return "primary";
		}

		if (recolector.comprobado === 1 && recolector.respuesta_comprobacion === 1) {
			return "secondary";
		}

		if (recolector.comprobado === 1 && recolector.respuesta_comprobacion === 0) {
			return "error";
		}

		return "default";
	};

	const hasValidFichajeScore = (recolector) => {
		const comprobado = Number(recolector?.comprobado ?? 0);
		const respuesta = Number(recolector?.respuesta_comprobacion ?? 0);
		const puntuacion = Number(recolector?.puntuacion_comprobacion);

		return (
			comprobado === 1 &&
			respuesta === 1 &&
			Number.isFinite(puntuacion) &&
			puntuacion > 0
		);
	};

	const getFichajeImageEndpoint = (recolector) => {
		if (!hasValidFichajeScore(recolector)) {
			return "";
		}

		if (typeof recolector?.url_imagen_endpoint === "string" && recolector.url_imagen_endpoint.trim()) {
			return recolector.url_imagen_endpoint.trim();
		}

		if (
			typeof recolector?.url_imagen !== "string" ||
			!recolector.url_imagen.trim() ||
			!recolector?.id_empleo
		) {
			return "";
		}

		return `https://recon.jcinformatics.com/imagefichaje.php?id_empleo=${recolector.id_empleo}`;
	};

	const getIntentoImageEndpoint = (intento) => {
		if (
			typeof intento?.url_imagen_endpoint === "string" &&
			intento.url_imagen_endpoint.trim()
		) {
			return intento.url_imagen_endpoint.trim();
		}

		const idIntento = Number(intento?.id_intento ?? 0);
		const imagenArchivo = typeof intento?.imagen_archivo === "string" ? intento.imagen_archivo.trim() : "";

		if (!Number.isFinite(idIntento) || idIntento <= 0 || !imagenArchivo) {
			return "";
		}

		return `https://recon.jcinformatics.com/imagefichaje.php?id_intento=${idIntento}`;
	};

	const getRecolectorDisplayImage = (recolector) => {
		const fichajeImage = getFichajeImageEndpoint(recolector);

		if (fichajeImage) {
			return fichajeImage;
		}

		if (typeof recolector?.imagen_url === "string" && recolector.imagen_url.trim()) {
			return recolector.imagen_url.trim();
		}

		return null;
	};

	const getRecolectorCoordinates = (recolector) => {
		const lat = String(
			recolector?.lat ??
			recolector?.latitud ??
			""
		).trim();
		const long = String(
			recolector?.long ??
			recolector?.longitud ??
			recolector?.lng ??
			recolector?.lon ??
			""
		).trim();

		return {
			lat,
			long,
			hasCoordinates: lat !== "" && long !== "",
		};
	};

	const getRecolectorMapUrl = (recolector) => {
		const { lat, long, hasCoordinates } = getRecolectorCoordinates(recolector);

		if (!hasCoordinates) {
			return "";
		}

		return `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${long}`)}`;
	};

	const totalRecolectores =
		info.recolectores.no_comprobados.length +
		info.recolectores.comprobados.length +
		info.recolectores.error_comprobacion.length;
	const recolectoresNoComprobados = useMemo(
		() => sortRecolectoresByName(info?.recolectores?.no_comprobados),
		[info?.recolectores?.no_comprobados]
	);
	const recolectoresComprobados = useMemo(
		() => sortRecolectoresByName(info?.recolectores?.comprobados),
		[info?.recolectores?.comprobados]
	);
	const recolectoresErrorComprobacion = useMemo(
		() => sortRecolectoresByName(info?.recolectores?.error_comprobacion),
		[info?.recolectores?.error_comprobacion]
	);
	const canUseProtectedActions = hasAuthSession();
	const intentosNoPertenece = Array.isArray(info?.intentos_no_pertenece)
		? info.intentos_no_pertenece
		: [];
	const intentosNoRegistrado = Array.isArray(info?.intentos_no_registrado)
		? info.intentos_no_registrado
		: [];

	const getPersonaAvatar = (recolector) => ({
		id: recolector.id_usuario,
		nombre: recolector.nombre_recolector,
		apellidos: recolector.apellidos_recolector,
		nombre_completo: `${recolector.nombre_recolector ?? ""} ${recolector.apellidos_recolector ?? ""}`.trim(),
		nif: recolector.nif_recolector,
		imagen_url: recolector.imagen_url || null,
	});

	const getRecolectorPreviewPersona = (recolector) => ({
		...getPersonaAvatar(recolector),
		imagen_url: getRecolectorDisplayImage(recolector),
	});

	const buildPersonaDetailState = (persona) => ({
		persona,
		from: "/cuadrillas",
		fromState: {
			...(personaReturnState || {}),
			reopenCuadrillaId: idCuadrilla,
		},
	});

	const getDetectedPersonaLinkData = (intento) => {
		const idDetectado = Number(intento?.id_usuario_detectado ?? 0);
		if (!Number.isFinite(idDetectado) || idDetectado <= 0) return;

		const persona = {
			id: idDetectado,
			nombre: intento?.nombre_detectado ?? "",
			apellidos: intento?.apellidos_detectado ?? "",
			nombre_completo: `${intento?.nombre_detectado ?? ""} ${intento?.apellidos_detectado ?? ""}`.trim(),
			nif: intento?.nif_detectado ?? "",
			imagen_url: `https://recon.jcinformatics.com/image.php?id_usuario=${idDetectado}`,
		};

		return {
			to: `/personas/${idDetectado}`,
			state: buildPersonaDetailState(persona),
		};
	};

	const abrirImagenFichaje = (recolector) => {
		if (!recolector) return;

		const endpoint = getFichajeImageEndpoint(recolector);
		if (!endpoint) return;

		const nombre = `${recolector.nombre_recolector ?? ""} ${recolector.apellidos_recolector ?? ""}`.trim();
		const puntuacion = formatScoreOverHundred(recolector.puntuacion_comprobacion);

		setFichajePreview({
			title: "Imagen del fichaje",
			nombre,
			subtitle: `${nombre || "Recolector"} - ${puntuacion}`,
			puntuacion,
			endpoint,
		});
	};

	const abrirImagenIntento = (intento, title = "Imagen del intento") => {
		if (!intento) return;

		const endpoint = getIntentoImageEndpoint(intento);
		if (!endpoint) return;

		const idIntento = Number(intento?.id_intento ?? 0);
		const idDetectado = Number(intento?.id_usuario_detectado ?? 0);
		const detectedNombre = `${intento?.nombre_detectado ?? ""} ${intento?.apellidos_detectado ?? ""}`.trim();
		const detectedNif = `${intento?.nif_detectado ?? ""}`.trim();

		const nombre = detectedNombre
			? `${detectedNombre}${detectedNif ? ` - ${detectedNif}` : ""}`
			: Number.isFinite(idDetectado) && idDetectado > 0
				? `Usuario #${idDetectado}`
				: Number.isFinite(idIntento) && idIntento > 0
					? `Intento #${idIntento}`
					: "Intento";

		const detailParts = [];
		if (intento?.probability !== undefined && intento?.probability !== null) {
			detailParts.push(`prob.: ${formatScoreOverHundred(intento.probability)}`);
		}
		if (intento?.liveness_score !== undefined && intento?.liveness_score !== null) {
			detailParts.push(`liveness: ${formatScoreOverHundred(intento.liveness_score)}`);
		}

		setFichajePreview({
			title,
			nombre,
			subtitle: detailParts.length ? `${nombre} - ${detailParts.join(" · ")}` : nombre,
			endpoint,
		});
	};

	const openFilePicker = () => {
		if (cpUploading) return;
		fileInputRef.current?.click();
	};

	const handleCpPorFoto = async (file) => {
		if (!file) return;
		if (!hasAuthSession()) {
			setCpResult({
				success: false,
				reason: "token_missing",
				message: "No hay sesión activa.",
			});
			return;
		}

		if (!idCuadrilla) {
			setCpResult({
				success: false,
				reason: "missing_id_cuadrilla",
				message: "Falta id_cuadrilla.",
			});
			return;
		}

		setCpUploading(true);
		setCpResult(null);

		try {
			const resp = await cpPorFoto({ id_cuadrilla: idCuadrilla, foto: file });

			setCpResult(resp || null);

			if (resp?.success) {
				if (typeof onRefetchDetalle === "function") {
					await onRefetchDetalle();
				}
			}
		} catch (err) {
			console.error("Error cpPorFoto:", err);

			const backend = err?.response?.data;
			if (backend && typeof backend === "object") {
				setCpResult(backend);
			} else {
				setCpResult({
					success: false,
					reason: "network_error",
					message: "Error de red al comprobar la foto.",
				});
			}
		} finally {
			setCpUploading(false);
		}
	};

	const handleFileChange = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";

		if (!file) return;

		if (!file.type.startsWith("image/")) {
			setCpResult({
				success: false,
				reason: "invalid_file",
				message: "Selecciona una imagen válida.",
			});
			return;
		}

		await handleCpPorFoto(file);
	};

	const cerrarImagenFichaje = () => {
		setFichajePreview(null);
	};

	useEffect(() => {
		if (parentDialogOpen === false) {
			setFichajePreview(null);
			setCpResult(null);
		}
	}, [parentDialogOpen]);

	useEffect(() => {
		let active = true;
		let objectUrl = null;

	const cargarImagenFichaje = async () => {
			if (!fichajePreview?.endpoint) {
				setFichajePreviewSrc("");
				setFichajePreviewLoading(false);
				setFichajePreviewError("");
				return;
			}

			setFichajePreviewLoading(true);
			setFichajePreviewError("");
			setFichajePreviewSrc("");

			try {
				const separator = fichajePreview.endpoint.includes("?") ? "&" : "?";
				const endpoint = `${fichajePreview.endpoint}${separator}t=${Date.now()}`;
				const res = await authFetch(endpoint, {
					headers: {
						"Cache-Control": "no-cache",
						Pragma: "no-cache",
					},
				});

				if (!res.ok) throw new Error("No se pudo cargar la imagen del fichaje");

				const blob = await res.blob();
				objectUrl = URL.createObjectURL(blob);

				if (active) {
					setFichajePreviewSrc(objectUrl);
				}
			} catch (error) {
				console.error("Error cargando imagen del fichaje:", error);
				if (active) {
					setFichajePreviewSrc("");
					setFichajePreviewError("No se pudo cargar la imagen del fichaje");
				}
			} finally {
				if (active) {
					setFichajePreviewLoading(false);
				}
			}
		};

		cargarImagenFichaje();

		return () => {
			active = false;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [fichajePreview?.endpoint]);

	const renderRecolector = (recolector, index, sectionKey, highlightError = false) => {
		const tieneImagenFichaje = Boolean(getFichajeImageEndpoint(recolector));
		const mapUrl = getRecolectorMapUrl(recolector);
		const { lat, long, hasCoordinates } = getRecolectorCoordinates(recolector);
		const isCompact = sectionKey === "no-comprobados" || highlightError;

		return (
			<Grid
				key={`${sectionKey}-${recolector.id_empleo ?? index}`}
				container
				direction="row"
				justifyContent="start"
				alignItems="center"
				size={{ xs: 12 }}
				sx={{ py: isCompact ? 0.5 : 1.5, rowGap: isCompact ? 0.5 : 1 }}
			>
				<Grid
					container
					direction="row"
					justifyContent="start"
					alignItems="center"
					size={{ xs: 12 }}
					sx={{ columnGap: 1, flexWrap: "nowrap" }}
				>
					<AvatarPersona
						persona={getRecolectorPreviewPersona(recolector)}
						size={isCompact ? 24 : 32}
						allowUpload={Boolean(capabilities?.canUploadAvatar) && !tieneImagenFichaje}
						allowDeleteLuxand={
							Boolean(capabilities?.canDeleteAvatarLuxand) && !tieneImagenFichaje
						}
						parentDialogOpen={parentDialogOpen}
						onAvatarUploaded={(updatedPersona) => {
							if (typeof onRecolectorAvatarUploaded === "function") {
								onRecolectorAvatarUploaded({
									idCuadrilla: info.datos_cuadrilla.id_cuadrilla,
									idUsuario: recolector.id_usuario,
									imagenUrl: updatedPersona.imagen_url,
								});
							}
						}}
						onAvatarDeleted={(updatedPersona) => {
							if (typeof onRecolectorAvatarUploaded === "function") {
								onRecolectorAvatarUploaded({
									idCuadrilla: info.datos_cuadrilla.id_cuadrilla,
									idUsuario: recolector.id_usuario,
									imagenUrl: updatedPersona?.imagen_url || "",
								});
							}
						}}
					/>

					<Typography
						variant={isCompact ? "body2" : "body1"}
						color={highlightError ? "error" : "inherit"}
						component={recolector?.id_usuario ? RouterLink : "span"}
						to={recolector?.id_usuario ? `/personas/${recolector.id_usuario}` : undefined}
						state={
							recolector?.id_usuario
								? buildPersonaDetailState(getPersonaAvatar(recolector))
								: undefined
						}
						sx={{
							fontWeight: highlightError ? 600 : 400,
							fontSize: isCompact ? "0.85rem" : undefined,
							cursor: recolector?.id_usuario ? "pointer" : "default",
							color: "inherit",
							textDecoration: "none",
							"&:hover": {
								textDecoration: "underline",
								color: highlightError ? "error.main" : "primary.main",
							},
						}}
					>
						{recolector.nombre_recolector} {recolector.apellidos_recolector} -{" "}
						{recolector.nif_recolector}
					</Typography>
				</Grid>

				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
					<Chip
						icon={<SensorOccupiedIcon />}
						label={
							!recolector.puntuacion_comprobacion
								? "0/100"
								: formatScoreOverHundred(recolector.puntuacion_comprobacion)
						}
						size="small"
						color={getColor(recolector)}
						clickable={tieneImagenFichaje}
						onClick={tieneImagenFichaje ? () => abrirImagenFichaje(recolector) : undefined}
						title={
							tieneImagenFichaje
								? "Ver imagen asociada al fichaje"
								: "Este fichaje no tiene imagen asociada"
						}
						sx={{
							cursor: tieneImagenFichaje ? "pointer" : "default",
							height: isCompact ? 24 : undefined,
						}}
					/>

					{hasCoordinates && (
						<Button
							component="a"
							href={mapUrl}
							target="_blank"
							rel="noopener noreferrer"
							size="small"
							variant="outlined"
							startIcon={<LocationOnIcon />}
							title={`Abrir ubicacion del fichaje (${lat}, ${long})`}
							sx={{
								minWidth: 0,
								px: 1,
								py: 0.25,
								textTransform: "none",
								lineHeight: 1.2,
								fontSize: isCompact ? "0.72rem" : "0.8rem",
							}}
						>
							Google Maps
						</Button>
					)}
				</Box>
			</Grid>
		);
	};

	const renderIntentoNoPertenece = (intento, index) => {
		const idIntento = intento?.id_intento ?? index;
		const idDetectado = Number(intento?.id_usuario_detectado ?? 0);
		const detectedNombre = `${intento?.nombre_detectado ?? ""} ${intento?.apellidos_detectado ?? ""}`.trim();
		const detectedNif = `${intento?.nif_detectado ?? ""}`.trim();
		const detectedLabel = detectedNombre
			? `${detectedNombre}${detectedNif ? ` - ${detectedNif}` : ""}`
			: Number.isFinite(idDetectado) && idDetectado > 0
				? `Usuario #${idDetectado}`
				: "";
		const detectedPersonaLinkData = getDetectedPersonaLinkData(intento);

		const intentoImageEndpoint = getIntentoImageEndpoint(intento);
		const tieneImagenIntento = Boolean(intentoImageEndpoint);

		return (
			<Grid
				key={`intento-no-pertenece-${idIntento}`}
				container
				direction="row"
				justifyContent="start"
				alignItems="center"
				size={{ xs: 12 }}
				sx={{ py: 0.5, rowGap: 0.5 }}
			>
				<Grid
					container
					direction="row"
					justifyContent="start"
					alignItems="center"
					size={{ xs: 12 }}
					sx={{ columnGap: 1, flexWrap: "nowrap" }}
				>
					<Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
						No reconocido
					</Typography>
				</Grid>

				{detectedLabel && (
					<Typography
						variant="caption"
						color="text.secondary"
						component={detectedPersonaLinkData ? RouterLink : "span"}
						to={detectedPersonaLinkData?.to}
						state={detectedPersonaLinkData?.state}
						sx={{
							fontSize: "0.8rem",
							cursor:
								Number.isFinite(idDetectado) && idDetectado > 0 ? "pointer" : "default",
							color: "inherit",
							textDecoration: "none",
							"&:hover": {
								textDecoration:
									Number.isFinite(idDetectado) && idDetectado > 0 ? "underline" : "none",
								color:
									Number.isFinite(idDetectado) && idDetectado > 0
										? "primary.main"
										: "text.secondary",
							},
						}}
					>
						Detectado: {detectedLabel}
					</Typography>
				)}

				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
					<Chip size="small" color="error" variant="outlined" label="no pertenece" />

					<Chip
						icon={<PhotoCameraIcon />}
						label="Foto"
						size="small"
						clickable={tieneImagenIntento}
						onClick={
							tieneImagenIntento
								? () => abrirImagenIntento(intento, "Imagen del intento (no pertenece al parte)")
								: undefined
						}
						title={
							tieneImagenIntento
								? "Ver foto asociada al intento"
								: "Este intento no tiene foto asociada"
						}
						sx={{
							cursor: tieneImagenIntento ? "pointer" : "default",
						}}
					/>

					{intento?.probability !== undefined && intento?.probability !== null && (
						<Chip
							size="small"
							variant="outlined"
							label={`prob.: ${formatScoreOverHundred(intento.probability)}`}
						/>
					)}

					{intento?.liveness_score !== undefined && intento?.liveness_score !== null && (
						<Chip
							size="small"
							variant="outlined"
							label={`liveness: ${formatScoreOverHundred(intento.liveness_score)}`}
						/>
					)}

					{typeof intento?.uuid_luxand === "string" && intento.uuid_luxand.trim() && (
						<Chip
							size="small"
							variant="outlined"
							label={`uuid: ${intento.uuid_luxand.trim()}`}
						/>
					)}
				</Box>
			</Grid>
		);
	};

	const renderIntentoNoRegistrado = (intento, index) => {
		const idIntento = intento?.id_intento ?? index;
		const intentoImageEndpoint = getIntentoImageEndpoint(intento);
		const tieneImagenIntento = Boolean(intentoImageEndpoint);

		return (
			<Grid
				key={`intento-no-registrado-${idIntento}`}
				container
				direction="row"
				justifyContent="start"
				alignItems="center"
				size={{ xs: 12 }}
				sx={{ py: 0.5, rowGap: 0.5 }}
			>
				<Grid
					container
					direction="row"
					justifyContent="start"
					alignItems="center"
					size={{ xs: 12 }}
					sx={{ columnGap: 1, flexWrap: "nowrap" }}
				>
					<Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
						No reconocido
					</Typography>
				</Grid>

				<Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
					Persona detectada no registrada en el sistema
				</Typography>

				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
					<Chip size="small" color="error" variant="outlined" label="no registrado" />

					<Chip
						icon={<PhotoCameraIcon />}
						label="Foto"
						size="small"
						clickable={tieneImagenIntento}
						onClick={
							tieneImagenIntento
								? () =>
										abrirImagenIntento(
											intento,
											"Imagen del intento (no registrado en sistema)"
										)
								: undefined
						}
						title={
							tieneImagenIntento
								? "Ver foto asociada al intento"
								: "Este intento no tiene foto asociada"
						}
						sx={{
							cursor: tieneImagenIntento ? "pointer" : "default",
						}}
					/>

					{intento?.probability !== undefined && intento?.probability !== null && (
						<Chip
							size="small"
							variant="outlined"
							label={`prob.: ${formatScoreOverHundred(intento.probability)}`}
						/>
					)}

					{intento?.liveness_score !== undefined && intento?.liveness_score !== null && (
						<Chip
							size="small"
							variant="outlined"
							label={`liveness: ${formatScoreOverHundred(intento.liveness_score)}`}
						/>
					)}

					{typeof intento?.uuid_luxand === "string" && intento.uuid_luxand.trim() && (
						<Chip
							size="small"
							variant="outlined"
							label={`uuid: ${intento.uuid_luxand.trim()}`}
						/>
					)}
				</Box>
			</Grid>
		);
	};

	return (
		<Grid
			container
			direction="row"
			justifyContent="start"
			alignItems="center"
			size={{ xs: 12 }}
			sx={{
				p: 2,
				mb: 2,
			}}
		>
			<Grid
				container
				direction="row"
				justifyContent="space-between"
				alignItems="start"
				size={{ xs: 12 }}
			>
				{cpResult && (
					<Grid item size={{ xs: 12 }} sx={{ mb: 1 }}>
						<Alert
							severity={
								cpResult?.success
									? "success"
									: suplantacionUi?.title
										? suplantacionUi.severity
										: "error"
							}
						>
							<Typography variant="subtitle2" sx={{ mb: 0.25 }}>
								{cpResult?.success
									? "Validación por foto completada"
									: suplantacionUi?.title || "No se pudo validar por foto"}
							</Typography>
							<Typography variant="body2" sx={{ opacity: 0.85 }}>
								{cpResult?.message ||
									suplantacionUi?.description ||
									(cpResult?.success
										? "Se ha actualizado el estado del recolector."
										: "Revisa el motivo en el detalle.")}
							</Typography>

							{cpDetails.length > 0 && (
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
									{cpDetails.map((d) => (
										<Chip
											key={d.key}
											size="small"
											variant="outlined"
											label={`${d.label}: ${d.value}`}
										/>
									))}
								</Box>
							)}
						</Alert>
					</Grid>
				)}

				<Grid
					container
					direction="column"
					justifyContent="center"
					alignItems="start"
					size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
					gap={1}
				>
					<Grid
						container
						direction="row"
						justifyContent="start"
						alignItems="start"
						size={{ xs: 12 }}
					>
						<GroupWorkIcon sx={{ fontSize: 30 }} color="warning" />
						<Typography variant="h5">
							&nbsp;Grupo N: {info.datos_cuadrilla.id_cuadrilla}
						</Typography>
					</Grid>

					<Typography variant="h6">
						<EventIcon sx={{ mb: -1, fontSize: 30 }} color="primary" />{" "}
						{formatDateTimeEs(info.datos_cuadrilla.fecha_cuadrilla)}
					</Typography>

					<Typography variant="subtitle1">
						<AssignmentIndIcon sx={{ mb: -1, fontSize: 30 }} color="secondary" /> Cabo:{" "}
						{info.datos_cuadrilla.nombre_cabo} {info.datos_cuadrilla.apellidos_cabo}
					</Typography>

					<Typography variant="subtitle1">
						<GroupsIcon sx={{ mb: -1, fontSize: 30 }} color="warning" /> Campaña:{" "}
						{info.datos_cuadrilla.nombre_campana || "Sin campaña"}
					</Typography>

					<Typography variant="subtitle1">
						<BusinessIcon sx={{ mb: -1, fontSize: 30 }} color="action" /> Delegacion:{" "}
						{info.datos_cuadrilla.nombre_delegacion || "Sin delegacion"}
					</Typography>

					<Typography variant="subtitle1">
						<GroupsIcon sx={{ mb: -1, fontSize: 30 }} color="inherit" /> Recolectores:{" "}
						{totalRecolectores}
					</Typography>
				</Grid>

				<Grid
					container
					direction="column"
					justifyContent="start"
					alignItems={{ xs: "start", md: "end" }}
					size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
					gap={1}
				>
					{capabilities?.canUsePhotoCheck && (
						<Grid item size={{ xs: 8, md: 6, lg: 3 }}>
							<Button
								variant="contained"
								fullWidth
								startIcon={<PhotoCameraIcon />}
								disabled={!idCuadrilla || !canUseProtectedActions || cpUploading}
								onClick={openFilePicker}
								title={
									!canUseProtectedActions
										? "Inicia sesion para comprobar por foto"
										: !idCuadrilla
											? "Falta id_cuadrilla"
											: "Subir una foto para validar"
								}
							>
								{cpUploading ? "Comprobando..." : "Comprobar por foto"}
							</Button>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/*"
								onChange={handleFileChange}
								style={{ display: "none" }}
							/>
						</Grid>
					)}

					{capabilities?.canEditEstado && (
						<>
							<Grid item size={{ xs: 8, md: 6, lg: 1.5 }}>
								<FormControl variant="standard" fullWidth size="small">
									<InputLabel id="cuadrilla-validado-label">Validado</InputLabel>
									<Select
										labelId="cuadrilla-validado-label"
										id="cuadrilla-validado"
										label="Validado"
										value={estadoParte.validado}
										onChange={(event) =>
											handleEstadoChange({ field: "validado", value: event.target.value })
										}
									>
										<MenuItem value={0}>No</MenuItem>
										<MenuItem value={1}>Si</MenuItem>
									</Select>
								</FormControl>
							</Grid>

							<Grid item size={{ xs: 8, md: 6, lg: 1.5 }}>
								<FormControl variant="standard" fullWidth size="small">
									<InputLabel id="cuadrilla-revisado-label">Revisado</InputLabel>
									<Select
										labelId="cuadrilla-revisado-label"
										id="cuadrilla-revisado"
										label="Revisado"
										value={estadoParte.revisado}
										onChange={(event) =>
											handleEstadoChange({ field: "revisado", value: event.target.value })
										}
									>
										<MenuItem value={0}>No</MenuItem>
										<MenuItem value={1}>Si</MenuItem>
									</Select>
								</FormControl>
							</Grid>
						</>
					)}
				</Grid>
			</Grid>

			<Grid
				container
				direction="row"
				justifyContent="start"
				alignItems="start"
				size={{ xs: 12 }}
				sx={{ mt: 2 }}
			>
				<Grid
					container
					direction="column"
					justifyContent="start"
					alignItems="start"
					size={{ xs: 12, md: 6, lg: 4 }}
					sx={{ display: info.recolectores.no_comprobados.length === 0 ? "none" : "flex", pr: 2, pb: 2 }}
				>
					<Typography variant="subtitle2">
						No comprobados ({recolectoresNoComprobados.length}):
					</Typography>

					<Divider />
					{recolectoresNoComprobados.map((recolector, index) =>
						renderRecolector(recolector, index, "no-comprobados")
					)}
				</Grid>

				<Grid
					container
					direction="column"
					justifyContent="start"
					alignItems="start"
					size={{ xs: 12, md: 6, lg: 4 }}
					sx={{ display: info.recolectores.comprobados.length === 0 ? "none" : "flex", pr: 2, pb: 2 }}
				>
					<Typography variant="subtitle2">
						Comprobados ({recolectoresComprobados.length}):
					</Typography>

					<Divider />
					{recolectoresComprobados.map((recolector, index) =>
						renderRecolector(recolector, index, "comprobados")
					)}
				</Grid>

				<Grid
					container
					direction="column"
					justifyContent="start"
					alignItems="start"
					size={{ xs: 12, md: 6, lg: 4 }}
					sx={{ display: info.recolectores.error_comprobacion.length === 0 ? "none" : "flex", pr: 2, pb: 2 }}
				>
					<Typography variant="subtitle2">
						Error en la comprobacion ({recolectoresErrorComprobacion.length}):
					</Typography>

					<Divider />
					{recolectoresErrorComprobacion.map((recolector, index) =>
						renderRecolector(recolector, index, "error-comprobacion", true)
					)}
				</Grid>

				<Grid
					container
					direction="column"
					justifyContent="start"
					alignItems="start"
					size={{ xs: 12, md: 6, lg: 4 }}
					sx={{ display: intentosNoPertenece.length === 0 ? "none" : "flex", pr: 2, pb: 2 }}
				>
					<Typography variant="subtitle2">
						No reconocidos (no pertenece al parte) ({intentosNoPertenece.length}):
					</Typography>

					<Divider />
					{intentosNoPertenece.map((intento, index) =>
						renderIntentoNoPertenece(intento, index)
					)}
				</Grid>

				<Grid
					container
					direction="column"
					justifyContent="start"
					alignItems="start"
					size={{ xs: 12, md: 6, lg: 4 }}
					sx={{ display: intentosNoRegistrado.length === 0 ? "none" : "flex", pr: 2, pb: 2 }}
				>
					<Typography variant="subtitle2">
						No reconocidos (no registrados en el sistema) ({intentosNoRegistrado.length}):
					</Typography>

					<Divider />
					{intentosNoRegistrado.map((intento, index) =>
						renderIntentoNoRegistrado(intento, index)
					)}
				</Grid>
			</Grid>

			<Divider />

			<Grid
				container
				direction="row"
				justifyContent="start"
				alignItems="end"
				size={{ xs: 12 }}
				sx={{ minHeight: "10vh" }}
			>
				<LeyendaRecolectores />
			</Grid>

			{parentDialogOpen && fichajePreview && (
				<Dialog open onClose={cerrarImagenFichaje} maxWidth="md" fullWidth>
					<DialogTitle>{fichajePreview?.title || "Imagen"}</DialogTitle>

					<DialogContent sx={{ display: "flex", justifyContent: "center", p: 2 }}>
						<Grid container direction="column" spacing={2}>
							<Grid item size={{ xs: 12 }}>
								<Typography variant="subtitle1">
									{fichajePreview.subtitle || fichajePreview.nombre || "Recolector"}
								</Typography>
							</Grid>

							<Grid item size={{ xs: 12 }}>
								{fichajePreviewLoading ? (
									<Box
										sx={{
											minHeight: "35vh",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<CircularProgress />
									</Box>
								) : fichajePreviewError ? (
									<Alert severity="error">{fichajePreviewError}</Alert>
								) : (
									<Box
										component="img"
										src={fichajePreviewSrc}
										alt={`Imagen de ${fichajePreview.nombre || "recolector"}`}
										sx={{
											maxWidth: "100%",
											maxHeight: "70vh",
											objectFit: "contain",
											borderRadius: 1,
										}}
									/>
								)}
							</Grid>
						</Grid>
					</DialogContent>

					<DialogActions>
						<Button onClick={cerrarImagenFichaje}>Cerrar</Button>
					</DialogActions>
				</Dialog>
			)}
		</Grid>
	);
};
